// Property management → Arrears (the demo's pmArrears): this development's tenancies behind on rent, read through the
// same /arrears list and summary as the Arrears page, narrowed to the property. The tiles are filters (?pmf=due |
// overMonth); a row opens the tenancy's arrears in a side sheet here, with the ladder, the letter and the history.
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { EmptyState } from '../ui';
import { StatTile, FilterNote } from '../charts/StatTile';
import { ArrearsList } from '../arrears/ArrearsList';
import { ArrearsDrawer } from '../arrears/ArrearsDrawer';
import { arrearsApi } from '../../lib/arrears';
import { formatMoney } from '../../lib/viz';

export type ArrearsFilter = 'due' | 'overMonth';
const LABEL: Record<ArrearsFilter, string> = { due: 'tenancies due a reminder today', overMonth: 'tenancies more than a month behind' };

export function ArrearsPanel({ propertyId, filter, onFilter }: { propertyId: string; filter: ArrearsFilter | null; onFilter: (f: ArrearsFilter | null) => void }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const summary = useQuery({ queryKey: ['/arrears/summary', propertyId], queryFn: () => arrearsApi.summary({ propertyId }) });
  const list = useQuery({
    queryKey: ['/arrears', propertyId, filter],
    queryFn: () => arrearsApi.list({ propertyId, due: filter === 'due', minDays: filter === 'overMonth' ? 31 : undefined }),
  });
  const refresh = (msg: string) => {
    for (const k of ['/arrears', '/arrears/summary', '/arrears/detail', 'management-summary']) void qc.invalidateQueries({ queryKey: [k] });
    setToast(msg); setTimeout(() => setToast(null), 4000);
  };
  const s = summary.data;
  const toggle = (f: ArrearsFilter) => onFilter(filter === f ? null : f);

  return (
    <div className="space-y-3">
      <div className="tile-row">
        <StatTile compact label="Outstanding" value={s ? formatMoney(s.owedMinor, s.currency) : '—'} status={s?.owedMinor ? 'critical' : undefined} hint={filter ? 'show them all' : undefined} onClick={() => onFilter(null)} />
        <StatTile compact label="Flats behind" value={s?.count ?? '—'} onClick={() => onFilter(null)} />
        <StatTile compact label="Reminders due" value={s?.dueNow ?? '—'} status={s?.dueNow ? 'warning' : undefined} active={filter === 'due'} onClick={() => toggle('due')} />
        <StatTile compact label="Over a month" value={s?.overMonth ?? '—'} status={s?.overMonth ? 'critical' : undefined} active={filter === 'overMonth'} onClick={() => toggle('overMonth')} />
      </div>
      <p className="text-[12px] text-ink-500">
        Anything unpaid past its due day lands here and climbs the ladder at 7, 14, 21 and 31 days.{' '}
        <Link to={`/app/arrears?propertyId=${propertyId}`} className="font-medium text-bronze-600 hover:underline">Open on the Arrears page</Link> to run the ladder.
      </p>
      {filter && <FilterNote label={LABEL[filter]} count={list.data?.length} onClear={() => onFilter(null)} />}
      {list.isLoading ? (
        <div className="surface space-y-3 p-4">{[0, 1, 2].map((i) => <div key={i} className="h-14 animate-pulse rounded-xl bg-ink-100" />)}</div>
      ) : list.error ? (
        <EmptyState icon="TriangleAlert" title="Could not load the arrears" body={(list.error as Error).message} />
      ) : !list.data?.length ? (
        filter
          ? <EmptyState icon="Filter" title="Nothing matches" body="No tenancy here matches that tile." />
          : <EmptyState icon="CircleCheck" title="Nobody is behind" body="Every instalment on this development past its due date has been paid." />
      ) : (
        <ArrearsList rows={list.data} activeId={open} onOpen={(r) => setOpen(r.tenancyId)} />
      )}
      {open && <ArrearsDrawer tenancyId={open} onClose={() => setOpen(null)} onChanged={refresh} />}
      {toast && <div className="fixed inset-x-4 bottom-4 z-[60] mx-auto max-w-md rounded-lg bg-ink-800 px-4 py-3 text-[13px] text-on-primary shadow-pop" role="status">{toast}</div>}
    </div>
  );
}
